import { router } from '@inertiajs/react'
import { Cpu, Info, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CatalogStatusBar, type CatalogStatus } from '@/frontend/components/Litellm/CatalogStatusBar'
import { ModelChipList } from '@/frontend/components/Litellm/ModelChipList'
import { ModelPickerPopover } from '@/frontend/components/Litellm/ModelPickerPopover'
import { ModelMetaBadges, ModelProviderTag } from '@/frontend/components/Litellm/ModelBadges'
import { SpendWidget } from '@/frontend/components/Litellm/SpendWidget'
import type { LitellmModel } from '@/frontend/components/Litellm/types'

type LlmProvider = 'bedrock' | 'litellm'
type FallbackTarget = 'anthropic' | 'bedrock' | 'litellm'

interface WorkerRef {
  id: string
  instance_name: string
  llm_provider: LlmProvider
  provisioning_status: string | null
  litellm_models: string[]
  effective_litellm_models: string[]
  fallback_target: FallbackTarget | null
  effective_fallback_target: FallbackTarget
  effective_fallback_models: string[]
}

const FALLBACK_TARGET_LABELS: Record<FallbackTarget, string> = {
  anthropic: 'Anthropic',
  bedrock: 'Bedrock',
  litellm: 'LiteLLM',
}

interface Props {
  worker: WorkerRef
  defaultModels: string[]
  catalog: LitellmModel[]
  catalogStatus?: CatalogStatus
}

const PROVIDERS: { value: LlmProvider; label: string; description: string }[] = [
  {
    value: 'bedrock',
    label: 'Bedrock',
    description: 'Provider nativo definido no openclaw.json.',
  },
  {
    value: 'litellm',
    label: 'LiteLLM',
    description: 'Gateway LiteLLM com virtual key, orçamento e chain de fallback por agente.',
  },
]

export default function LlmProviderTab({ worker, defaultModels, catalog, catalogStatus }: Props) {
  const [provider, setProvider] = useState<LlmProvider>(worker.llm_provider)
  const initialModels = worker.litellm_models.length ? worker.litellm_models : defaultModels
  const [models, setModels] = useState<string[]>(initialModels)
  // Empty string represents "inherit global default" (persisted as null).
  const [fallbackTarget, setFallbackTarget] = useState<FallbackTarget | ''>(worker.fallback_target ?? '')
  const [saving, setSaving] = useState(false)
  const disabled = worker.provisioning_status !== 'ready'

  // Reset local selection when the underlying worker payload changes (e.g.,
  // after a save round-trip Inertia replaces the props in place).
  useEffect(() => {
    setProvider(worker.llm_provider)
    setModels(worker.litellm_models.length ? worker.litellm_models : defaultModels)
    setFallbackTarget(worker.fallback_target ?? '')
  }, [worker.id, worker.llm_provider, worker.litellm_models.join('|'), worker.fallback_target, defaultModels.join('|')])

  const submit = () => {
    setSaving(true)
    router.post(
      `/cc/instances/${worker.id}/update_llm_settings`,
      { llm_provider: provider, litellm_models: models, fallback_target: fallbackTarget },
      { preserveScroll: true, onFinish: () => setSaving(false) },
    )
  }

  const effective = worker.effective_litellm_models.length ? worker.effective_litellm_models : defaultModels
  const catalogByName = new Map(catalog.map((m) => [m.model_name, m]))
  const primaryModel = models[0]
  const primaryMeta = primaryModel ? catalogByName.get(primaryModel) : undefined

  const usingDefaults = worker.litellm_models.length === 0

  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-md border border-border bg-muted/50 p-2">
            <Cpu className="h-5 w-5 text-foreground" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">LLM</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Configuração ativa em <code className="font-mono text-xs">{worker.instance_name}</code>.
              {disabled && (
                <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                  Instância precisa estar <code className="font-mono">ready</code> para alterar.
                </span>
              )}
            </p>
          </div>
        </div>
        <Button size="sm" onClick={submit} disabled={disabled || saving}>
          <Save className="h-4 w-4" />
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Provider</div>
        <div className="grid gap-2 md:grid-cols-2">
          {PROVIDERS.map((option) => {
            const active = provider === option.value
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setProvider(option.value)}
                disabled={disabled}
                className={`group flex items-start gap-3 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  active
                    ? 'border-foreground bg-accent text-accent-foreground'
                    : 'border-border bg-background hover:bg-accent/50'
                }`}
              >
                <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 transition-colors ${
                  active ? 'border-foreground bg-foreground' : 'border-border'
                }`} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{option.label}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{option.description}</div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Fallback de modelo</div>
        <select
          value={fallbackTarget}
          onChange={(event) => setFallbackTarget(event.target.value as FallbackTarget | '')}
          disabled={disabled}
          className="w-full max-w-sm rounded-md border bg-background px-3 py-2 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="">Herdar padrão ({FALLBACK_TARGET_LABELS[worker.effective_fallback_target]})</option>
          <option value="anthropic">Anthropic</option>
          <option value="bedrock">Bedrock</option>
          <option value="litellm">LiteLLM</option>
        </select>
        <p className="text-xs text-muted-foreground">
          Fallback efetivo:{' '}
          <span className="font-semibold text-foreground">
            {FALLBACK_TARGET_LABELS[fallbackTarget || worker.effective_fallback_target]}
          </span>
          {worker.effective_fallback_models.length > 0 && (
            <>
              {' '}—{' '}
              <code className="font-mono text-[11px]">{worker.effective_fallback_models.join(', ')}</code>
            </>
          )}
        </p>
      </div>

      {provider === 'litellm' && (
        <div className="mt-5 space-y-5 rounded-lg border border-border bg-background p-4">
          <CatalogStatusBar catalog={catalog} status={catalogStatus} />

          <SpendWidget workerId={worker.id} />

          <div>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-semibold text-foreground">Modelos do agente</div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  O primeiro modelo é o primary. Demais entram na fallback chain do LiteLLM.
                  {usingDefaults && (
                    <span className="ml-1 text-amber-600 dark:text-amber-400">
                      Herdando os defaults globais — salve para fixar nessa instância.
                    </span>
                  )}
                </p>
              </div>
              <ModelPickerPopover
                catalog={catalog}
                selectedNames={models}
                onPick={(name) => setModels([...models, name])}
                disabled={disabled}
                label="Adicionar modelo"
              />
            </div>

            <ModelChipList
              selected={models}
              catalog={catalog}
              onChange={setModels}
              disabled={disabled}
              showPrimary
              catalogAvailable={catalog.length > 0}
              emptyHint="Sem modelos selecionados — ao salvar, a instância cai para os defaults globais."
            />
          </div>

          {primaryModel && (
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <div className="mb-2 flex items-center gap-2">
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Primary atual</div>
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-sm text-foreground">{primaryModel}</span>
                  {primaryMeta && <ModelProviderTag model={primaryMeta} />}
                </div>
                {primaryMeta ? (
                  <ModelMetaBadges model={primaryMeta} />
                ) : (
                  <Badge variant="warning" className="font-normal">
                    Modelo não encontrado no catálogo do gateway.
                  </Badge>
                )}
              </div>
            </div>
          )}

          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Resolução efetiva no gateway
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {effective.map((name, index) => (
                <div key={`${name}-${index}`} className="flex items-center gap-1.5">
                  {index > 0 && <span className="text-xs text-muted-foreground">→</span>}
                  <Badge variant={index === 0 ? 'default' : 'info'} className="font-mono font-normal">
                    {name}
                  </Badge>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              O que o agente realmente usa quando dispara um completion. Trocar o modelo físico em um slot é instantâneo (sem restart). Adicionar/remover slot exige reprovisionar.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
